// A1-74 — one line per row on a review card.
//
// Operator, 2026-10-11, with seven screenshots: "there is too much info
// here that creates noise. its repetitive. when i inspect i get the info
// i need, why have it here as well." Each row used to carry its order,
// date, route, amount, an "also flagged" badge, a truncated transaction
// id and up to four buttons — and the Inspect drawer shows every one of
// those again. So a row is now the order and the amount (what the
// operator recognises it by) followed by the controls that change its
// outcome, all on ONE line. Date, route, the other flags and the id live
// in Inspect, which is where the operator said they look.
//
// The controls sit in a single flex row on purpose: the reverse-charge
// card used to render "Inspect" and "Where is the customer?" as two
// stacked blocks.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { fmtMoney } from "../../lib/format";
import { RefundAuditView } from "./RefundAuditView";

// A2-579 tweak 3 (2026-10-27): light column headers, so mixed-currency rows scan.
export const ReviewRowHeader = () => {
  const { t } = useTranslation();
  return (
    <div data-testid="rg-row-header" aria-hidden
         className="col-span-full grid grid-cols-[minmax(7rem,11rem)_minmax(6rem,9rem)_1fr] gap-x-4 pb-1 border-b border-slate-200 dark:border-slate-700 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
      <span>{t("ui.review_grouped.col_invoice", { defaultValue: "Invoice" })}</span>
      <span className="text-right">{t("ui.review_grouped.col_amount", { defaultValue: "Amount" })}</span>
      <span>{t("ui.review_grouped.col_actions", { defaultValue: "Select · actions" })}</span>
    </div>
  );
};

// A1-104 (operator 2026-10-31: "it would help to see the actual VAT rate"):
// under the amount, the VAT, its rate and the country it is taxed in.
export const rowFacts = (net, vat, place) => {
  const n = Number(net); const v = Number(vat);
  if (!Number.isFinite(n) || !Number.isFinite(v) || net === "" || vat === "") return null;
  const rate = n !== 0 ? `${Number(((v / n) * 100).toFixed(2))}%` : null;
  return [`VAT ${fmtMoney(vat)}`, rate, place].filter(Boolean).join(" · ");
};

// A1-105 (a) — "where the row lands": the boxes of the return it fills,
// worked out by the return's own rules (api/_row_boxes), e.g. "GB box 1, 6".
export const landsIn = (boxes) => {
  const byCountry = {};
  for (const b of boxes || []) {
    (byCountry[b.country] = byCountry[b.country] || []).push(b.box || b.label);
  }
  return Object.entries(byCountry).map(([cc, refs]) => `${cc} ${refs.join(", ")}`).join(" · ");
};

const RowBoxesLabel = ({ text }) => {
  const { t } = useTranslation();
  return t("ui.review_answers.lands_in", { list: text, defaultValue: "Return boxes: {{list}}" });
};

export const ReviewRowLine = ({
  tid, anchorId, orderId, gross, currency, net, vat, place, boxes, children,
}) => (
  <div
    id={anchorId}
    data-testid={`rg-row-${tid}`}
    className="col-span-full grid grid-cols-[minmax(7rem,11rem)_minmax(6rem,9rem)_1fr] items-center gap-x-4 gap-y-1 py-1.5 border-b border-slate-100 dark:border-slate-800 last:border-0"
  >
    {/* Operator, 2026-10-11, on the first version: "gap between
        transaction value and inspect is huge, do it at least half way.
        Applies for all the function buttons". The controls were pushed
        to the far edge with ml-auto, so the eye crossed the whole screen
        from an amount to its button. Three columns instead: order |
        amount | controls, the controls starting right after the amount
        and every amount right-aligned so a column of figures scans. */}
    <span
      data-testid={`rg-row-order-${tid}`}
      className="text-xs text-slate-700 dark:text-slate-200 truncate"
      translate="no"
    >
      {orderId || "—"}
    </span>
    <span
      data-testid={`rg-row-amount-${tid}`}
      className="text-xs tabular-nums text-right text-slate-500 dark:text-slate-400"
    >
      {gross !== undefined && gross !== null ? `${fmtMoney(gross)} ${currency || ""}` : ""}
      {rowFacts(net, vat, place) && (
        <span data-testid={`rg-row-facts-${tid}`} className="block text-[11px] text-slate-500 dark:text-slate-400">
          {rowFacts(net, vat, place)}
        </span>
      )}
      {boxes && boxes.length > 0 && (
        <span data-testid={`rg-row-boxes-${tid}`} className="block text-[11px] text-slate-600 dark:text-slate-300">
          <RowBoxesLabel text={landsIn(boxes)} />
        </span>
      )}
    </span>
    <div data-testid={`rg-row-controls-${tid}`} className="flex items-center gap-2 flex-wrap">
      {children}
    </div>
  </div>
);

// A1-74 — the credit-note box, in one plain sentence.
//
// The full RefundAuditView spoke the engine's language ("Inherited
// channel: National return (§17)", "channel-equivalent — specific
// original unresolved", "operator manually assigned channel (tier-3)")
// and repeated once per row. What the operator needs from it is one
// fact: is this credit note tied to its sale, and if not, does it
// matter? The full view stays one click away for anyone auditing the
// pairing itself.
export const refundSentence = (pairing, t) => {
  if (!pairing) return null;
  const candidates = pairing.candidates || [];
  // A1-88: a saved record keeps the first 25 and says how many there were
  const count = pairing.candidate_count ?? candidates.length;
  if (pairing.validation === "manual") {
    return t("ui.review_grouped.refund_sentence_manual",
      { defaultValue: "You chose which return this credit note is filed on." });
  }
  if (pairing.resolved_specific && candidates.length > 0) {
    return t("ui.review_grouped.refund_sentence_linked",
      { order: candidates[0], defaultValue: "Linked to the original sale {{order}}." });
  }
  if (count > 1) {
    return t("ui.review_grouped.refund_sentence_candidates", {
      n: count,
      defaultValue: "Matches {{n}} possible original sales, all filed the "
        + "same way, so the figures are right. Add the original invoice number "
        + "in 'Reverses invoice' to link it to one.",
    });
  }
  return t("ui.review_grouped.refund_sentence_none",
    { defaultValue: "No original sale found for this credit note." });
};

export const RefundSummary = ({ pairing, tid }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const sentence = refundSentence(pairing, t);
  if (!sentence) return null;
  return (
    <div className="col-span-full pl-1 pb-1.5 text-xs text-slate-600 dark:text-slate-300">
      <span data-testid={`rg-refund-sentence-${tid}`}>{sentence}</span>{" "}
      <button
        type="button"
        data-testid={`rg-refund-details-${tid}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="underline text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100"
      >
        {open
          ? t("ui.review_grouped.refund_details_hide", { defaultValue: "Hide details" })
          : t("ui.review_grouped.refund_details_show", { defaultValue: "Details" })}
      </button>
      {open && <RefundAuditView pairing={pairing} />}
    </div>
  );
};
